const STORAGE_KEY = "desktopOAuth";
const DESKTOP_CLIENT_ID = "sagentx-desktop";

function isAllowedDesktopRedirect(uri) {
  const value = String(uri || "");
  if (value.startsWith("superagentx://")) return true;
  try {
    const parsed = new URL(value);
    const host = parsed.hostname;
    const isLoopback =
      host === "127.0.0.1" || host === "localhost" || host === "[::1]";
    return parsed.protocol === "http:" && isLoopback;
  } catch {
    return false;
  }
}

export function captureDesktopOAuthFromSearch(search) {
  const params = new URLSearchParams(search || window.location.search);
  if (params.get("source") !== "desktop") return null;

  const payload = {
    source: "desktop",
    responseType: params.get("response_type") || "code",
    clientId: params.get("client_id") || DESKTOP_CLIENT_ID,
    redirectUri: params.get("redirect_uri") || "",
    codeChallenge: params.get("code_challenge") || "",
    codeChallengeMethod: params.get("code_challenge_method") || "S256",
    state: params.get("state") || "",
    scope: params.get("scope") || "openid profile email",
  };

  if (!payload.redirectUri || !payload.codeChallenge || !payload.state) {
    return null;
  }

  if (!isAllowedDesktopRedirect(payload.redirectUri)) {
    return null;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    /* ignore */
  }
  return payload;
}

export function getDesktopOAuth() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

export function isDesktopOAuth() {
  const oauth = getDesktopOAuth();
  return Boolean(oauth?.source === "desktop" && oauth.redirectUri && oauth.state && oauth.codeChallenge);
}

export function clearDesktopOAuth() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function preserveDesktopOAuthAcrossClear() {
  const oauth = getDesktopOAuth();
  return () => {
    if (oauth) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(oauth));
      } catch (e) {
        /* ignore */
      }
    }
  };
}

function buildDesktopCallbackUrl(redirectUri, { code, state }) {
  const base = String(redirectUri || "").split("?")[0];
  const params = new URLSearchParams();
  params.set("code", code);
  params.set("state", state || "");
  return `${base}?${params.toString()}`;
}

/**
 * Hand control back to the Electron app after web login succeeds.
 * Prefers a backend-issued PKCE auth code; falls back to the access token as `code`
 * (desktop accepts JWT codes until /auth0/oauth/token authorize is available).
 */
export async function completeDesktopOAuth({ accessToken, authorizeFn }) {
  const oauth = getDesktopOAuth();
  if (!oauth || oauth.source !== "desktop") return false;
  if (!accessToken) throw new Error("Missing access token for desktop login");

  let code = null;

  if (typeof authorizeFn === "function") {
    try {
      const response = await authorizeFn({
        clientId: oauth.clientId || DESKTOP_CLIENT_ID,
        redirectUri: oauth.redirectUri,
        codeChallenge: oauth.codeChallenge,
        codeChallengeMethod: oauth.codeChallengeMethod || "S256",
        state: oauth.state,
        scope: oauth.scope,
        accessToken,
      });
      code = response?.code || response?.authorizationCode || response?.data?.code || null;
    } catch (e) {
      code = null;
    }
  }

  // Fallback: pass the session JWT as the authorization code.
  if (!code) {
    code = accessToken;
  }

  clearDesktopOAuth();
  window.location.href = buildDesktopCallbackUrl(oauth.redirectUri, {
    code,
    state: oauth.state,
  });
  return true;
}

export function cancelDesktopOAuth({ error = "access_denied", description } = {}) {
  const oauth = getDesktopOAuth();
  if (!oauth?.redirectUri) return false;
  clearDesktopOAuth();
  const base = String(oauth.redirectUri).split("?")[0];
  const params = new URLSearchParams();
  params.set("error", error);
  params.set("state", oauth.state || "");
  if (description) params.set("error_description", description);
  window.location.href = `${base}?${params.toString()}`;
  return true;
}
